import { action } from '@ember/object';
import { prepareCheckoutNewCardPaymentEmbed } from 'client-portal/utils/checkout-new-card-payment';
import { success } from 'client-portal/utils/banners';
import { task } from 'ember-concurrency';
import Service, { service } from '@ember/service';

// Card checkout can pay with a new unsaved card. Other instrument types must be saved first.
export default class PaymentInstrumentModalsService extends Service {
  @service billingModals;
  @service store;
  @service stripe;
  @service intl;

  get isPersisting() {
    return this.persistPaymentTask.isRunning;
  }

  persistPayment(stripeElement = null) {
    return this.persistPaymentTask.perform(stripeElement);
  }

  showPayment(invoice = null) {
    const payment = this.store.createRecord('payment');
    const instrument = this.defaultSavedCard;

    this.setPaymentInstrument(payment, instrument, { _invoice: invoice });

    this.billingModals.set('paymentModel', payment);
    this.billingModals.setPaymentInvoice(invoice);
  }

  @action
  hidePayment() {
    this.discardEphemeralPaymentInstrument(this.billingModals.paymentModel);
    this.billingModals.paymentModel?.deleteRecord();
    this.billingModals.set('paymentModel', null);
  }

  get defaultSavedCard() {
    const { cards } = this.billingModals.client;

    return cards.find(({ isDefault }) => isDefault) || cards.at(0);
  }

  discardEphemeralPaymentInstrument(payment) {
    const instrument = payment?.stripePaymentInstrument;

    if (instrument?.isNew) {
      instrument.deleteRecord();
    }
  }

  setPaymentInstrument(payment, instrument, additionalProperties = {}) {
    payment.setProperties({
      stripePaymentInstrument: instrument,
      ...additionalProperties,
    });
  }

  @(task(function* (elements) {
    const { paymentModel } = this.billingModals;

    if (this.billingModals.isPaymentInvalid) return;

    paymentModel.set('_isDirty', true);
    this.billingModals._trackClientPaymentAttempt('Attempted');

    const instrument = paymentModel.stripePaymentInstrument;
    let embed = [];

    try {
      if (instrument?.isNew && instrument.constructor.modelName === 'card') {
        embed = yield prepareCheckoutNewCardPaymentEmbed({
          elements,
          instrument,
          paymentModel,
          stripe: this.stripe.stripe,
        });
        if (embed === null) return;
      }

      yield this.billingModals._createPayment(embed);
      yield Promise.all(this.billingModals.afterPersist.map(x => x()));

      this.stripe.setup(this.billingModals.currentPractice.get('customStripePublishableKey'));

      this.hidePayment();
      success({ title: this.intl.t('billing.success.paymentMadeSuccessfully') });
    } catch (err) {
      this.billingModals._handlePaymentFailure(err);
    }
  }).keepLatest())
  persistPaymentTask;
}
